import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Facebook, Link2, Check, Share2, ExternalLink, PartyPopper, Loader2, Lock, Home, ClipboardList, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { listingShareUrl, buyerNeedShareUrl, facebookShare, copyToClipboard } from "@/lib/share";
import { trackEvent } from "@/lib/track";
import { ShareDialog } from "@/components/ShareDialog";

const peso = (n) => (typeof n === "number" && n > 0 ? `₱${n.toLocaleString()}` : null);

// Post-Publish Distribution P1 (Properties + Buyer Requirements).
// Uses ONLY the authoritative canonical public URL (/listing/{id} or /buyer-need/{id}).
// Public actions render only when the authoritative entity is publicly eligible
// (confirmed by the PII-safe public endpoint returning 200; 404 => not shareable).
export function PostPublishDistribution({ open, onOpenChange, kind, id, onDone }) {
  const isProperty = kind === "property";
  const entityType = isProperty ? "property" : "buyer_requirement";
  const [state, setState] = useState("loading"); // loading | eligible | ineligible | error
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  const url = isProperty ? listingShareUrl(id) : buyerNeedShareUrl(id);
  const meta = { entity_type: entityType, entity_id: id };

  const load = useCallback(() => {
    if (!id) return;
    setState("loading");
    const endpoint = isProperty ? `/public/listings/${id}` : `/public/buyer-needs/${id}`;
    api.get(endpoint)
      .then((r) => { setData(r.data); setState("eligible"); })
      .catch((e) => setState(e?.response?.status === 404 ? "ineligible" : "error"));
  }, [id, isProperty]);

  useEffect(() => { if (open) { setCopied(false); setMoreOpen(false); load(); } }, [open, load]);
  useEffect(() => {
    if (open && (state === "eligible" || state === "ineligible")) {
      trackEvent("distribution_prompt_shown", { ...meta, metadata: { eligible: state === "eligible" } });
    }
  }, [open, state]); // eslint-disable-line

  const shareEvent = isProperty ? "listing_shared" : "buyer_need_shared";

  const doFacebook = async () => {
    trackEvent("share_facebook_selected", meta);
    trackEvent(shareEvent, { ...meta, metadata: { channel: "facebook" } });
    const res = await facebookShare({ url, title });
    if (res === "copied") { setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Couldn't open Facebook here — link copied. Paste it into Facebook to share."); }
    else if (res === "failed") toast.error("Couldn't open Facebook — try Copy Link.");
  };
  const doCopy = async () => {
    trackEvent("share_copy_link", meta);
    const ok = await copyToClipboard(url);
    if (ok) { setCopied(true); trackEvent(shareEvent, { ...meta, metadata: { channel: "copy_link" } }); toast.success("Link copied"); setTimeout(() => setCopied(false), 2000); }
    else toast.error("Could not copy the link");
  };
  const goView = () => { onOpenChange(false); onDone?.(url, true); };
  const done = () => { onOpenChange(false); onDone?.(url, false); };

  const title = isProperty
    ? (data?.title || "Your listing")
    : (data?.title || data?.buyer_alias || data?.property_type || "Your Buyer Need");
  const price = isProperty
    ? (data?.listing_type === "Unit Hosting" ? (peso(data?.nightly_rate) ? `${peso(data.nightly_rate)}/night` : null) : peso(data?.price))
    : (peso(data?.budget_min) && peso(data?.budget_max) ? `${peso(data.budget_min)} – ${peso(data.budget_max)}`
        : peso(data?.budget_max) ? `Up to ${peso(data.budget_max)}` : peso(data?.budget_min) ? `From ${peso(data.budget_min)}` : null);
  const location = isProperty
    ? [data?.city, data?.neighborhood].filter(Boolean).join(", ")
    : (Array.isArray(data?.cities) ? data.cities.slice(0, 2).join(", ") : (data?.city || data?.location || ""));

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="post-publish-dialog" className="w-[calc(100vw-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PartyPopper className="h-5 w-5 text-[#C79A3B]" /> {isProperty ? "Listing published!" : "Buyer Need posted!"}
            </DialogTitle>
            <DialogDescription>
              {state === "eligible" ? "Share it to reach more people and get faster responses." : "Manage and share your post."}
            </DialogDescription>
          </DialogHeader>

          {state === "loading" && <div className="flex justify-center py-10 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>}

          {state === "error" && (
            <div className="text-center py-8 text-sm text-slate-500" data-testid="post-publish-error">
              Couldn't load sharing options right now. <button onClick={load} className="text-[#0F2C59] font-semibold underline underline-offset-2">Retry</button>
            </div>
          )}

          {state === "ineligible" && (
            <div className="text-center py-6 px-2" data-testid="post-publish-ineligible">
              <div className="mx-auto h-11 w-11 rounded-full bg-slate-100 grid place-items-center mb-3"><Lock className="h-5 w-5 text-slate-500" /></div>
              <p className="text-sm text-slate-600">This is not set to <span className="font-semibold">Public Marketplace</span>, so it isn't publicly shareable yet. Set it to Public Marketplace to share it.</p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <Button data-testid="post-publish-manage" variant="outline" onClick={goView} className="rounded-xl">Manage</Button>
                <Button data-testid="post-publish-done" onClick={done} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white">Done</Button>
              </div>
            </div>
          )}

          {state === "eligible" && (
            <div className="space-y-4">
              {/* compact preview of what is being shared (authoritative public data) */}
              <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="post-publish-preview">
                {isProperty && data?.image && <img src={data.image} alt={title} className="w-full h-36 object-cover" />}
                <div className="p-3">
                  <div className="flex items-center gap-1.5 text-[11px] text-[#C79A3B] font-semibold uppercase tracking-wide">
                    {isProperty ? <Home className="h-3.5 w-3.5" /> : <ClipboardList className="h-3.5 w-3.5" />}
                    {isProperty ? (data?.listing_type || "Property") : "Buyer Need"}
                  </div>
                  <div className="font-semibold text-[#0F2C59] mt-1 line-clamp-2" data-testid="post-publish-title">{title}</div>
                  {location && <div className="text-[13px] text-slate-500 mt-0.5 flex items-center gap-1"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{location}</span></div>}
                  {price && <div className="text-[#0F2C59] font-bold mt-1" data-testid="post-publish-price">{price}</div>}
                  <div className="mt-2 text-[11px] text-slate-400 truncate" data-testid="post-publish-url">{url}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button data-testid="post-publish-facebook" onClick={doFacebook} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white">
                  <Facebook className="h-4 w-4 mr-1.5" /> Share to Facebook
                </Button>
                <Button data-testid="post-publish-copy" variant="outline" onClick={doCopy} className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59]">
                  {copied ? <Check className="h-4 w-4 mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />} {copied ? "Copied" : (isProperty ? "Copy Listing Link" : "Copy Buyer Need Link")}
                </Button>
              </div>

              <button data-testid="post-publish-more" onClick={() => setMoreOpen(true)} className="w-full flex items-center justify-center gap-1.5 text-sm font-semibold text-[#0F2C59] hover:underline">
                <Share2 className="h-4 w-4" /> More sharing options
              </button>

              <div className="flex items-center justify-between border-t border-slate-100 pt-3">
                <button data-testid="post-publish-view" onClick={goView} className="flex items-center gap-1.5 text-sm font-semibold text-[#C79A3B] hover:underline">
                  <ExternalLink className="h-4 w-4" /> {isProperty ? "View Listing" : "View Buyer Need"}
                </button>
                <Button data-testid="post-publish-done" onClick={done} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white">Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ShareDialog
        open={moreOpen}
        onOpenChange={setMoreOpen}
        url={url}
        title={title}
        text={title}
        entityId={id}
        heading={isProperty ? "Share this listing" : "Share this Buyer Need"}
        subtitle={isProperty
          ? "Share your public PropertyConnect listing with potential buyers and your professional network."
          : "Share your public PropertyConnect Buyer Need with sellers and your professional network."}
      />
    </>
  );
}
